<template>
  <div class="animated fadeIn">
    <b-card>
      <h1>Search Users</h1>

      <b-input
        v-model="userSearch"
        debounce="500"
        placeholder="search..."
        type="search"
        autofocus
      />
      <b-table
        ref="table"
        :fields="[
          { key: 'name', label: 'Name' },
          { key: 'org', label: 'Org' },
          { key: 'license', label: 'License' },
          { key: 'mara', label: 'MARA' }
        ]"
        :items="searchUsers"
        :filter="userSearch"
        :current-page="userPage"
        :per-page="10"
        class="mt-2 table-outline"
        borderless
        fixed
        striped
      >
        <template #cell(name)="{ item }">
          <div class="d-flex">
            <b-btn
              v-if="hasPermission('user.switch')"
              v-b-tooltip.hover="'Switch to this user'"
              variant="link"
              @click="borrowSession(item.email)"
            >
              <i class="icon-login" />
            </b-btn>
            <div class="ml-2">
              <div>{{ item.name || "-" }}</div>
              <div class="small text-muted">{{ item.email }}</div>
            </div>
          </div>
        </template>
        <template #cell(org)="{ item }">
          <div>{{ item.org_name || "-" }}</div>
          <div class="small text-muted">
            {{ item.org_roles || "" }}
          </div>
        </template>

        <template #cell(license)="{ item }">
          <div v-if="!item.license_type" class="text-muted">free</div>
          <div v-else>{{ item.license_type }}</div>
          <span
            v-if="item.license_expiration"
            v-b-tooltip.hover="
              new Date(item.license_expiration).toLocaleString()
            "
            :class="licenseExpirationColor(item.license_expiration)"
            class="small"
          >
            exp {{ fromNow(item.license_expiration) }}
          </span>
        </template>

        <template #cell(mara)="{ item }">
          <div class="d-flex justify-content-between">
            <div v-if="!item.mara_credits">-</div>
            <div v-else>
              <div>
                {{ item.mara_credits === -1 ? "∞" : item.mara_credits }}
                <span class="small">/ {{ item.mara_credits_max }}</span>
              </div>
              <div class="small">
                <span v-if="item.mara_premium" class="text-purple">
                  premium
                </span>
                <span v-else class="text-muted">free</span>
                <span
                  v-if="item.mara_premium"
                  v-b-tooltip.hover="
                    new Date(item.mara_premium_expires_at).toLocaleString()
                  "
                  :class="maraExpirationColor(item.mara_premium_expires_at)"
                  class="small"
                >
                  exp {{ fromNow(item.mara_premium_expires_at) }}
                </span>
              </div>
            </div>
            <b-btn
              v-if="hasPermission('user.edit')"
              v-b-tooltip.hover="'Edit MARA info'"
              variant="link"
              @click="selectedUser = item"
            >
              <i class="icon-pencil" />
            </b-btn>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="userTotal > 10"
        v-model="userPage"
        :total-rows="userTotal"
        :per-page="10"
        align="center"
        prev-text="Prev"
        next-text="Next"
        hide-goto-end-buttons
      />
    </b-card>

    <b-modal
      :visible="!!selectedUser"
      @hide="selectedUser = null"
      centered
      hide-header
      hide-footer
    >
      <AdminEditMara
        v-if="selectedUser"
        :user="selectedUser"
        @update="$refs.table.refresh()"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import API from "@/api";
import AdminEditMara from "@/views/admin/AdminEditMara";

export default {
  components: {
    AdminEditMara
  },

  data: () => ({
    selectedUser: null,
    userSearch: "",
    userPage: 1,
    userTotal: 0
  }),

  computed: {
    ...mapGetters(["isAdmin", "hasPermission"])
  },

  methods: {
    async borrowSession(email) {
      if (this.disabled) return;
      await this.$store.dispatch("borrowSession", { email });
      this.$router.push({ name: "Home" });
    },

    fromNow(time) {
      return moment(time).fromNow();
    },

    licenseExpirationColor(date) {
      if (!date) return "text-muted";
      const now = moment();
      if (now.add(14, "days").isAfter(date)) return "text-danger";
      if (now.add(14, "days").isAfter(date)) return "text-warning";
      return "text-success";
    },

    maraExpirationColor(date) {
      if (!date) return "text-muted";
      const now = moment();
      if (now.add(7, "days").isAfter(date)) return "text-danger";
      if (now.add(7, "days").isAfter(date)) return "text-warning";
      return "text-success";
    },

    async searchUsers(ctx) {
      this.selectedUser = null;
      const { success, results } = await API.adminUserSearch(
        this.userSearch,
        ctx.currentPage,
        ctx.perPage,
        true
      );
      if (!success) return;

      this.userTotal = results.total;
      return results.results;
    }
  }
};
</script>
